import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, Users, DollarSign } from 'lucide-react';
import { api } from '../lib/api';

function slugify(s) {
  return (s || '').toLowerCase()
    .replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g')
    .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [status, setStatus] = useState('devam');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      const [ongoing, completed] = await Promise.all([
        api.publicContent('ongoing_projects').catch(() => []),
        api.publicContent('completed_projects').catch(() => []),
      ]);
      if (cancel) return;
      const findIn = (list) => list
        .map(it => ({ id: it.id, ...(it.data || {}) }))
        .find(x => x.slug === slug || slugify(x.title) === slug);
      const found = findIn(ongoing);
      if (found) { setItem(found); setStatus('devam'); }
      else {
        const f2 = findIn(completed);
        if (f2) { setItem(f2); setStatus('tamamlanan'); }
      }
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, [slug]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-ink-400">Yükleniyor...</div>;
  if (!item) return (
    <div className="min-h-[60vh] grid place-items-center">
      <div className="text-center">
        <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">404</div>
        <h1 className="font-display text-4xl mt-3">Proje bulunamadı</h1>
        <Link to="/projeler" className="mt-5 inline-flex items-center gap-2 text-ink-500 hover:text-ink-600"><ArrowLeft className="w-4 h-4" /> Projelere dön</Link>
      </div>
    </div>
  );

  const isCompleted = status === 'tamamlanan';

  return (
    <>
      <section className="relative bg-ink-600 text-cream overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #C9A96E 0, transparent 55%), radial-gradient(circle at 80% 70%, #E8826F 0, transparent 55%)' }} />
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 pt-16 pb-20 md:pt-20 md:pb-28">
          <Link to={`/projeler?tab=${isCompleted ? 'tamamlanan' : 'devam'}`} className="inline-flex items-center gap-1.5 text-cream/60 hover:text-gold text-xs tracking-[0.25em] uppercase font-semibold transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> {isCompleted ? 'Tamamlanan Projeler' : 'Devam Eden Projeler'}
          </Link>
          <div className="mt-6 flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase">
            <span className={`px-3 py-1 rounded-full font-semibold ${isCompleted ? 'bg-emerald-500/20 text-emerald-300' : 'bg-gold/20 text-gold'}`}>{isCompleted ? '✓ Tamamlandı' : 'Devam Ediyor'}</span>
            {item.category && <span className="text-gold"><Tag className="w-3 h-3 inline mr-1" />{item.category}</span>}
            {item.date && <span className="text-cream/70"><Calendar className="w-3 h-3 inline mr-1" />{item.date}</span>}
          </div>
          <h1 className="font-display text-4xl md:text-6xl mt-5 leading-[1.05]">{item.title}</h1>
          {item.excerpt && <p className="mt-6 text-cream/75 text-base md:text-lg max-w-3xl">{item.excerpt}</p>}

          {(item.beneficiaries || item.budget) && (
            <div className="mt-8 flex flex-wrap gap-3">
              {item.beneficiaries && (
                <div className="bg-cream/5 border border-cream/15 rounded-xl px-4 py-2.5 inline-flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-gold" /> <span className="text-cream/70">Faydalanıcı:</span> <strong>{item.beneficiaries}</strong>
                </div>
              )}
              {item.budget && (
                <div className="bg-cream/5 border border-cream/15 rounded-xl px-4 py-2.5 inline-flex items-center gap-2 text-sm">
                  <DollarSign className="w-4 h-4 text-gold" /> <span className="text-cream/70">Bütçe:</span> <strong>{item.budget}</strong>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {(() => {
        const vid = String(item.video_id || '').trim();
        const isVideo = vid && (item.type === 'video' || !item.image);
        if (isVideo) {
          return (
            <div className="bg-cream">
              <div className="max-w-[1100px] mx-auto px-6 lg:px-10 -mt-14 md:-mt-20 relative">
                <div className="rounded-3xl overflow-hidden border border-ink-100 shadow-2xl shadow-ink-900/15 bg-ink-900 aspect-video" data-testid="project-video">
                  <iframe
                    title={item.title}
                    src={`https://www.youtube.com/embed/${vid}?rel=0`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          );
        }
        if (item.image) {
          return (
            <div className="bg-cream">
              <div className="max-w-[1100px] mx-auto px-6 lg:px-10 -mt-14 md:-mt-20 relative">
                <div className="rounded-3xl overflow-hidden border border-ink-100 shadow-2xl shadow-ink-900/15 bg-white">
                  {/* Show full uploaded image at natural aspect ratio — never crop */}
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-auto object-contain block"
                    style={{ maxHeight: 'calc(100vh - 120px)' }}
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      <section className="bg-cream">
        <div className="max-w-[800px] mx-auto px-6 lg:px-10 py-16 md:py-24">
          {String(item.video_id || '').trim() && item.type !== 'video' && item.image && (
            <div className="mb-10 rounded-2xl overflow-hidden border border-ink-100 shadow-lg aspect-video bg-ink-900" data-testid="project-video-inline">
              <iframe
                title={`${item.title} — video`}
                src={`https://www.youtube.com/embed/${String(item.video_id).trim()}?rel=0`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          {item.content ? (
            <div className="richtext" dangerouslySetInnerHTML={{ __html: item.content }} />
          ) : (
            <p className="text-ink-500 text-lg leading-relaxed">{item.excerpt}</p>
          )}

          <div className="mt-16 pt-10 border-t border-ink-100 flex flex-wrap items-center justify-between gap-4">
            <Link to={`/projeler?tab=${isCompleted ? 'tamamlanan' : 'devam'}`} className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-600 text-sm font-semibold">
              <ArrowLeft className="w-4 h-4" /> Tüm projeler
            </Link>
            <Link to="/bagis" className="inline-flex items-center gap-2 bg-gold text-ink-700 rounded-full px-6 h-12 font-semibold hover:bg-gold-400 transition-colors">
              Bu projeye destek ol →
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        .richtext{font-size:17px;line-height:1.75;color:#1A2744}
        .richtext h1{font-family:'Playfair Display',Georgia,serif;font-size:2.4rem;line-height:1.15;margin:1.4em 0 0.5em;color:#0F1E4D}
        .richtext h2{font-family:'Playfair Display',Georgia,serif;font-size:1.85rem;line-height:1.2;margin:1.3em 0 0.4em;color:#0F1E4D}
        .richtext h3{font-family:'Playfair Display',Georgia,serif;font-size:1.4rem;margin:1em 0 0.3em;color:#0F1E4D}
        .richtext p{margin:0.9em 0}
        .richtext ul,.richtext ol{padding-left:1.6em;margin:0.9em 0}
        .richtext ul li{list-style:disc;margin:0.35em 0}
        .richtext ol li{list-style:decimal;margin:0.35em 0}
        .richtext blockquote{border-left:3px solid #C9A96E;padding-left:1.25em;color:#374060;font-style:italic;margin:1.3em 0;font-size:1.18em}
        .richtext a{color:#0F1E4D;text-decoration:underline;text-underline-offset:3px}
        .richtext a:hover{color:#C9A96E}
        .richtext img{border-radius:0.75rem;max-width:100%;margin:1.4em 0}
        .richtext hr{border:0;border-top:1px solid #E8DECF;margin:1.8em 0}
        .richtext code{background:#F4EEE2;padding:0.15em 0.4em;border-radius:4px;font-size:0.9em}
      `}</style>
    </>
  );
}
